import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import type { KidProfile, KidTabProps } from "./types";

type OverviewData = Pick<
  KidProfile,
  "avgBattingScore" | "avgBowlingScore" | "avgFieldingScore" | "totalSessions" | "attendedSessions"
>;

interface Props extends KidTabProps<OverviewData> {}

function NarrativeSlot({ narrative }: { narrative: Props["narrative"] }) {
  if (!narrative) return null;
  return (
    <div className="rounded-lg bg-teal-50 border border-teal-200 px-4 py-3 text-sm text-teal-800 mb-5">
      {narrative}
    </div>
  );
}

export function KidOverviewTab({ data, isLoading, isEmpty, error, onRetry, narrative }: Props) {
  if (isLoading) {
    return (
      <div className="space-y-4 py-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-64 w-full rounded-xl" />
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
        <AlertCircle className="h-8 w-8 text-red-400" />
        <p className="text-sm text-gray-500">{error}</p>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        )}
      </div>
    );
  }

  if (isEmpty || (data.avgBattingScore == null && data.avgBowlingScore == null && data.avgFieldingScore == null)) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center text-gray-400">
        <p className="font-medium text-gray-500">No skill data yet</p>
        <p className="text-sm mt-1">Scores will appear after the first session.</p>
      </div>
    );
  }

  const radarData = [
    { discipline: "Batting", score: data.avgBattingScore ?? 0 },
    { discipline: "Bowling", score: data.avgBowlingScore ?? 0 },
    { discipline: "Fielding", score: data.avgFieldingScore ?? 0 },
  ];

  const attended = data.attendedSessions;
  const absent = data.totalSessions - data.attendedSessions;
  const attendancePct =
    data.totalSessions > 0 ? Math.round((attended / data.totalSessions) * 100) : 0;

  const donutData = [
    { name: "Attended", value: attended },
    { name: "Absent", value: absent },
  ];

  return (
    <div className="space-y-5 py-2" data-testid="tab-content-overview-loaded">
      <NarrativeSlot narrative={narrative} />

      <div className="grid md:grid-cols-2 gap-5">
        {/* Radar chart */}
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Skill Profile</h3>
          <ResponsiveContainer width="100%" height={220}>
            <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis
                dataKey="discipline"
                tick={{ fontSize: 13, fill: "#4b5563", fontWeight: 600 }}
              />
              <Radar
                dataKey="score"
                stroke="#3b82f6"
                fill="#3b82f6"
                fillOpacity={0.25}
                strokeWidth={2}
                dot={{ r: 4, fill: "#3b82f6" }}
              />
            </RadarChart>
          </ResponsiveContainer>
          <div className="flex justify-around mt-3 text-xs text-gray-500">
            {radarData.map(({ discipline, score }) => (
              <div key={discipline} className="text-center">
                <p className="font-semibold text-gray-800 text-sm">{score.toFixed(1)}</p>
                <p>{discipline}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Attendance donut */}
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex flex-col items-center justify-center">
          <h3 className="text-sm font-semibold text-gray-700 mb-2 self-start">Attendance</h3>
          <div className="relative">
            <ResponsiveContainer width={180} height={180}>
              <PieChart>
                <Pie
                  data={donutData}
                  cx="50%"
                  cy="50%"
                  innerRadius={58}
                  outerRadius={80}
                  startAngle={90}
                  endAngle={-270}
                  dataKey="value"
                  strokeWidth={0}
                >
                  <Cell fill="#3b82f6" />
                  <Cell fill="#e5e7eb" />
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            {/* Center label */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-2xl font-bold text-gray-900">{attendancePct}%</span>
              <span className="text-xs text-gray-400">attendance</span>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {attended} of {data.totalSessions} sessions attended
          </p>
        </div>
      </div>
    </div>
  );
}
